/**
 * Known browser-extension error signatures that reach Sentry but never
 * originate in application code.
 *
 * The production SPA loads inside operators' own browsers, so their installed
 * extensions and page translators run in the same window and throw into the
 * same global error handlers Sentry listens on. Those events carry no
 * application stack the team can act on, yet they crowd the inbox next to real
 * crashes and erode trust in it. Dropping them at `beforeSend` — the last hook
 * before an event leaves the client — keeps the versioned rule beside the SDK
 * init instead of scattering it across per-error guards.
 *
 * Extend {@link EXTENSION_NOISE_SIGNATURES} only with signatures proven to come
 * from a browser extension. Each entry is a plain substring, matched literally
 * against the event's message and its exception values/types; a signature that
 * could also describe an application error belongs in a component fix, not this
 * list.
 */

/**
 * Substrings that identify an extension-injected error.
 *
 * - `runtime.sendMessage` — the WebExtension messaging API this app never
 *   calls. "Invalid call to runtime.sendMessage()" / "Tab not found" is an
 *   extension talking to its own background page, not xan code (Sentry #484).
 */
export const EXTENSION_NOISE_SIGNATURES: readonly string[] = ["runtime.sendMessage"];

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;
}

function pushString(target: string[], value: unknown): void {
  if (typeof value === "string") {
    target.push(value);
  }
}

/** The value and type strings a single exception entry exposes. */
function exceptionEntryStrings(entry: unknown): string[] {
  const record = asRecord(entry);
  if (!record) {
    return [];
  }
  const strings: string[] = [];
  pushString(strings, record.value);
  pushString(strings, record.type);
  return strings;
}

/**
 * The human-readable strings an event exposes: its top-level message plus each
 * exception value and type. Everything else in a Sentry event is structured
 * metadata a signature would never legitimately match.
 */
function candidateStrings(event: unknown): string[] {
  const record = asRecord(event);
  if (!record) {
    return [];
  }

  const strings: string[] = [];
  pushString(strings, record.message);

  const rawValues = asRecord(record.exception)?.values;
  const values: unknown[] = Array.isArray(rawValues) ? rawValues : [];
  for (const entry of values) {
    strings.push(...exceptionEntryStrings(entry));
  }

  return strings;
}

/**
 * Whether an event matches a known browser-extension signature and should be
 * dropped before it leaves the client. Unknown event shapes never match, so a
 * genuine application error is always sent.
 */
export function isExtensionNoise(event: unknown): boolean {
  return candidateStrings(event).some((text) =>
    EXTENSION_NOISE_SIGNATURES.some((signature) => text.includes(signature)),
  );
}
